import { Injectable } from '@angular/core';
import { ADOBE_DATA_LAYER, GLOBAL_PAGE_VIEW } from 'axis-components';
import { Logger } from '../../_core/logger.service';
import { RequestService } from '../../_services/request/request.service';
import { FormType, RequestProgressStep } from '../../config/constants';
import { PipBaseRulesService as PIPBaseRulesService } from '../../state-disclosures/pip/services/pip-base-rules.service';
import { UimuiBaseRulesService as UMUIMBaseRulesService } from '../../state-disclosures/uimui/services/uimui-base-rules.service';
import {
  AdobeDataLayer,
  AdobeDataLayerCta,
  AdobeDataLayerDownload,
} from '../../common/constant';
import { GaragingSelectionType } from '../../config/pip-constants';
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  // Form state tracking
  public formViewUmSet = false;
  public formViewPipSet = false;
  public formStartUmSet = false;
  public formStartPipSet = false;
  // Prevent duplicate formComplete emissions during a single runtime.
  private readonly formCompleteEventKeys = new Set<string>();
  // Persist completion keys to avoid duplicate events on same-session reloads.
  private readonly FORM_COMPLETE_STORAGE_KEY = 'dgsn_form_complete_events';
  // Persist completion keys across tabs/sessions to enforce one completion per request/form.
  private readonly FORM_COMPLETE_GLOBAL_STORAGE_KEY =
    'dgsn_form_complete_events_global';

  adobeDataLayer: AdobeDataLayer = {
    event: 'pageView',
    site: {
      code: 'dgsn',
      country: 'us',
      language: 'en',
      platform: 'web',
      lob: '',
      businessSegment: '',
      environment: 'dev',
    },
    user: {
      userType: '',
      userId: '',
    },
    page: {
      pageName: 'DigiSign:Home',
      URL: '',
      fullURL: '',
      previousURL: window.location.origin,
    },
    form: {
      formType: '',
      step: '',
    },
    umuim: {
      bodilyInjury: '',
      coverages: '',
      limits: '',
      stackedOptions: '',
    },
    pip: {
      pipType: '',
      pipAdditional: '',
      pipAcceptance: '',
      pipCoverage: '',
    },
  };

  CtaObj: AdobeDataLayerCta = {
    event: 'siteInteraction',
    site: {
      code: 'dgsn',
      country: 'us',
      language: 'en',
      platform: 'web',
      lob: '',
      businessSegment: '',
      environment: 'dev',
    },
    user: {
      userType: '',
      userId: '',
    },
    page: {
      pageName: 'DigiSign:Home',
      URL: '',
      fullURL: '',
      previousURL: window.location.origin,
    },
    cta: {
      ctaType: 'button',
      ctaName: '',
      ctaURL: '',
    },
  };

  DownloadObj: AdobeDataLayerDownload = {
    event: 'fileDownload',
    site: {
      code: 'dgsn',
      country: 'us',
      language: 'en',
      platform: 'web',
      lob: '',
      businessSegment: '',
      environment: 'dev',
    },
    user: {
      userType: '',
      userId: '',
    },
    page: {
      pageName: 'DigiSign:Home',
      URL: '',
      fullURL: '',
      previousURL: window.location.origin,
    },
    download: {
      fileName: '',
      fileType: '',
      fileURL: '',
      fileFormat: 'pdf',
    },
  };
  createBaseAnalyticsObject() {
    this.adobeDataLayer = {
      event: 'pageView',
      site: {
        code: 'dgsn',
        country: 'us',
        language: 'en',
        platform: 'web',
        lob: '',
        businessSegment: '',
        environment: 'dev',
      },
      user: { userType: '', userId: '' },
      page: {
        pageName: '',
        URL: '',
        fullURL: '',
        previousURL: '',
      },
      form: {
        formType: '',
        step: '',
      },
      umuim: {
        bodilyInjury: '',
        coverages: '',
        limits: '',
        stackedOptions: '',
      },
      pip: {
        pipType: '',
        pipAdditional: '',
        pipAcceptance: '',
        pipCoverage: '',
      },
    };

    this.adobeDataLayer.site.environment = this.getEnvironment();

    if (!this.adobeDataLayer.form) {
      this.adobeDataLayer.form = {
        formType: '',
        step: '',
      };
    }

    return this.adobeDataLayer;
  }

  constructor(
    private requestService: RequestService,
    private uiumBaseRulesService: UMUIMBaseRulesService,
    private pipBaseRulesService: PIPBaseRulesService,
  ) {}

  // Move businessSegment to a getter method to avoid initialization order issues
  get businessSegment(): string {
    return (
      this.requestService.request?.requestDetails?.policySelection
        ?.productTower || ''
    );
  }

  getFormType(routeData) {
    if (routeData && routeData.page) {
      let url = routeData.page.URL;
      if (url) {
        let urlLower = url.toLowerCase();
        if (
          urlLower.includes('umuim') ||
          urlLower.includes('um_uim') ||
          urlLower.includes('um-uim') ||
          urlLower.includes('uimui')
        ) {
          return FormType.UM_UIM;
        }
      }
      let pageName = routeData.page.pageName;
      if (pageName) {
        let pageNameLower = pageName.toLowerCase();
        if (
          pageNameLower.includes('umuim') ||
          pageNameLower.includes('um_uim') ||
          pageNameLower.includes('um-uim') ||
          pageNameLower.includes('uimui')
        ) {
          return FormType.UM_UIM;
        }
      }
      if (pageName && pageName.toLowerCase().includes('pip')) {
        return FormType.PIP;
      }
    }
  }

  getEnvironment() {
    let url = window.location.hostname;
    if (url === 'digisign.aig.com' || url === 'coversign.aig.com') {
      return 'prod';
    }
    if (url.includes('dev.digisign')) {
      return 'dev';
    }
    if (url.includes('qa.digisign')) {
      return 'qa';
    }
    if (url.includes('uat.digisign')) {
      return 'uat';
    }
    return 'dev';
  }
  // updating adobeDataLayer using "data" attribute passed in routes from application.
  setAdobeDataLayer(data: any, currentStep?: string) {
    if (!window[ADOBE_DATA_LAYER]) {
      window[ADOBE_DATA_LAYER] = [];
    }

    // Create a new object instead of reusing this.adobeDataLayer
    const newAdobeDataLayer = JSON.parse(JSON.stringify(this.adobeDataLayer));
    newAdobeDataLayer.site.environment = this.getEnvironment();
    newAdobeDataLayer.site.businessSegment = this.businessSegment;
    newAdobeDataLayer.user = {
      userType: this.requestService.role,
      userId: this.requestService.requestKey || '',
    };

    if (data.hasOwnProperty('event')) {
      newAdobeDataLayer.event = data.event;
    }
    if (data.hasOwnProperty('page')) {
      newAdobeDataLayer.page = data.page;
    } else {
      newAdobeDataLayer.page = data;
    }

    // Use passed step or fall back to request service current step
    const stepToUse =
      currentStep || this.requestService.currentStep || 'Not Started';

    if (this.getFormType(data) === FormType.UM_UIM) {
      newAdobeDataLayer.form = {
        formType: this.getFormType(data) || 'UMUIM',
        step: stepToUse,
      };
      const obj = {
        bodilyInjury: this.uiumBaseRulesService.selectionTypeKey,
        limits: this.uiumBaseRulesService.limitTypeKey,
        stackedOptions: this.uiumBaseRulesService.stackTypeKey,
        coverages: this.uiumBaseRulesService.coverageTypeKey,
        garagingSelectionType: this.uiumBaseRulesService.garagingSelectionKey,
      };
      newAdobeDataLayer.umuim = obj;
    } else if (this.getFormType(data) === FormType.PIP) {
      newAdobeDataLayer.form = {
        formType: this.getFormType(data) || 'pip',
        step: stepToUse,
      };
      // Convert hidden/unselected PIP fields to empty string for Adobe payloads.
      const normalizePipValue = (value: unknown): string => {
        return value === undefined || value === null || value === 'Null'
          ? ''
          : String(value);
      };

      const pipAdditional: any = normalizePipValue(
        this.pipBaseRulesService.additionalOptions,
      );
      const pipCoverage: any = normalizePipValue(
        this.pipBaseRulesService.coverageLimit,
      );
      const obj = {
        pipType: this.pipBaseRulesService.personalInjuryProtectionSelection,
        pipAdditional,
        pipCoverage,
        garagingSelectionType: this.pipBaseRulesService.garagingSelectionKey,
      };
      newAdobeDataLayer.pip = obj;
    }

    window[ADOBE_DATA_LAYER].push(newAdobeDataLayer);

    // Update internal adobeDataLayer with current page info so CTA events use latest data
    this.adobeDataLayer.page = newAdobeDataLayer.page;
    this.adobeDataLayer.form = newAdobeDataLayer.form;
    this.adobeDataLayer.user = newAdobeDataLayer.user;
    this.adobeDataLayer.site = newAdobeDataLayer.site;
    if (newAdobeDataLayer.umuim) {
      this.adobeDataLayer.umuim = newAdobeDataLayer.umuim;
    }
    if (newAdobeDataLayer.pip) {
      this.adobeDataLayer.pip = newAdobeDataLayer.pip;
    }

    this.triggerGlobalEvent();
  }

  private triggerGlobalEvent() {
    const ev = new CustomEvent(GLOBAL_PAGE_VIEW);
    document.body.dispatchEvent(ev);
  }

  // Extract page object creation logic to avoid code duplication
  getCurrentPageObject(previousUrl: string = ''): any {
    return {
      pageName: this.adobeDataLayer.page.pageName,
      URL: `${window.location.origin}${window.location.pathname}`,
      fullURL: `${window.location.origin}${window.location.pathname}${window.location.search}`,
      previousURL: previousUrl
        ? `${window.location.origin}${previousUrl}`
        : this.adobeDataLayer.page.previousURL,
    };
  }

  private readonly URL_STEP_MAPPING = [
    {
      patterns: ['consent'],
      step: RequestProgressStep.DIGITAL_SIGNATURE_CONSENT,
    },
    {
      patterns: ['selection'],
      step: RequestProgressStep.SELECTION_OF_COVERAGES,
    },
    {
      patterns: ['review-state', 'state-summary', 'summary'],
      step: RequestProgressStep.REVIEW_STATE_SELECTIONS,
    },
    {
      patterns: ['outstanding-state', 'outstanding'],
      step: RequestProgressStep.OUTSTANDING_STATE_SELECTIONS,
    },
    { patterns: ['signature'], step: RequestProgressStep.ELECTRONIC_SIGNATURE },
    {
      patterns: ['thank-you', 'thankyou'],
      step: RequestProgressStep.THANK_YOU,
    },
    {
      patterns: ['non-digital'],
      step: RequestProgressStep.NON_DIGITAL_ELECTION_DOWNLOAD,
    },
  ];

  // Configuration for form type mapping
  private readonly FORM_TYPE_CONFIG = {
    [FormType.UM_UIM]: {
      defaultFormType: 'um_uim',
      serviceKey: 'uiumBaseRulesService',
      dataProperties: [
        'selectionTypeKey',
        'limitTypeKey',
        'stackTypeKey',
        'coverageTypeKey',
      ],
      dataKeys: ['bodilyInjury', 'limits', 'stackedOptions', 'coverages'],
    },
    [FormType.PIP]: {
      defaultFormType: 'pip',
      serviceKey: 'pipBaseRulesService',
      dataProperties: [
        'personalInjuryProtectionSelection',
        'additionalOptions',
        'coverageLimit',
      ],
      dataKeys: ['pipType', 'pipAdditional', 'pipCoverage'],
    },
  };

  // Helper method to determine step based on URL/page name for accurate step reporting
  getStepFromUrl(url: string, pageName: string = ''): string {
    const searchText = `${url.toLowerCase()} ${pageName.toLowerCase()}`;

    // Find matching step based on URL patterns
    const matchedMapping = this.URL_STEP_MAPPING.find((mapping) =>
      mapping.patterns.some((pattern) => searchText.includes(pattern)),
    );

    return (
      matchedMapping?.step || this.requestService.currentStep || 'Not Started'
    );
  }

  setAdobeDataLayerCTAEvent(data: any) {
    if (!window[ADOBE_DATA_LAYER]) {
      window[ADOBE_DATA_LAYER] = [];
    }

    // Start with current adobeDataLayer and override with CTA structure
    const ctaEventData = {
      event: this.CtaObj.event,
      site: {
        ...this.adobeDataLayer.site,
        environment: this.getEnvironment(),
        businessSegment: this.businessSegment,
      },
      user: {
        userType: this.requestService.role,
        userId: this.requestService.requestKey || '',
      },
      page: {
        ...this.adobeDataLayer.page,
        // No need to override URLs - they should already be current from setAdobeDataLayer updates
      },
      cta: {
        ctaType: 'button',
        ctaName: data.ctaName || '',
        ctaURL: data.ctaUrl || '',
      },
    };
    window[ADOBE_DATA_LAYER].push(ctaEventData);
    this.triggerGlobalEvent();
  }

  setAdobeDataLayerDownloadEvent(data: any) {
    if (!window[ADOBE_DATA_LAYER]) {
      window[ADOBE_DATA_LAYER] = [];
    }

    // Start with current adobeDataLayer and override with Download structure
    const downloadEventData = {
      event: this.DownloadObj.event,
      site: {
        ...this.adobeDataLayer.site,
        environment: this.getEnvironment(),
        businessSegment: this.businessSegment,
      },
      user: {
        userType: this.requestService.role,
        userId: this.requestService.requestKey || '',
      },
      page: {
        pageName: this.adobeDataLayer.page.pageName || 'DigiSign:ThankYou',
        URL: window.location.pathname,
        fullURL: window.location.href,
        previousURL:
          this.adobeDataLayer.page.previousURL || window.location.origin,
      },
      download: {
        fileName: data.fileName || '',
        fileType: data.fileType || '',
        fileURL: data.fileURL || window.location.href,
        fileFormat: data.fileFormat || 'pdf',
      },
    };
    window[ADOBE_DATA_LAYER].push(downloadEventData);
    this.triggerGlobalEvent();
  }

  private getStoredFormCompleteKeys(): string[] {
    // Session list covers reloads in the same browser session.
    try {
      const rawValue = globalThis.sessionStorage.getItem(
        this.FORM_COMPLETE_STORAGE_KEY,
      );
      if (!rawValue) {
        return [];
      }
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private saveStoredFormCompleteKeys(keys: string[]) {
    try {
      globalThis.sessionStorage.setItem(
        this.FORM_COMPLETE_STORAGE_KEY,
        JSON.stringify(keys),
      );
    } catch {
      // Ignore storage failures - in-memory dedupe still applies.
    }
  }

  private getStoredGlobalFormCompleteKeys(): string[] {
    // Global list covers tab-to-tab and new-session duplicates.
    try {
      const rawValue = globalThis.localStorage.getItem(
        this.FORM_COMPLETE_GLOBAL_STORAGE_KEY,
      );
      if (!rawValue) {
        return [];
      }
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private saveStoredGlobalFormCompleteKeys(keys: string[]) {
    try {
      globalThis.localStorage.setItem(
        this.FORM_COMPLETE_GLOBAL_STORAGE_KEY,
        JSON.stringify(keys),
      );
    } catch {
    }
  }

  private getFormCompleteLockKey(completionKey: string): string {
    // One lock key per request/form completion event.
    return `dgsn_form_complete_lock_${completionKey}`;
  }

  private tryAcquireFormCompleteLock(completionKey: string): string | null {
    // Last writer wins lock token to avoid two tabs firing at once.
    try {
      const lockKey = this.getFormCompleteLockKey(completionKey);
      const lockToken = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      globalThis.localStorage.setItem(lockKey, lockToken);
      const currentValue = globalThis.localStorage.getItem(lockKey);
      return currentValue === lockToken ? lockToken : null;
    } catch {
      return null;
    }
  }

  private releaseFormCompleteLock(completionKey: string, lockToken: string) {
    // Only the lock owner should clear the lock key.
    try {
      const lockKey = this.getFormCompleteLockKey(completionKey);
      const currentValue = globalThis.localStorage.getItem(lockKey);
      if (currentValue === lockToken) {
        globalThis.localStorage.removeItem(lockKey);
      }
    } catch {
      // Best-effort cleanup only.
    }
  }

  private hasFormCompleteFired(completionKey: string): boolean {
    // Check memory + local/session storage before attempting a new emit.
    if (this.formCompleteEventKeys.has(completionKey)) {
      return true;
    }

    const globalStoredKeys = this.getStoredGlobalFormCompleteKeys();
    if (globalStoredKeys.includes(completionKey)) {
      this.formCompleteEventKeys.add(completionKey);
      return true;
    }

    const storedKeys = this.getStoredFormCompleteKeys();
    if (storedKeys.includes(completionKey)) {
      this.formCompleteEventKeys.add(completionKey);
      return true;
    }

    return false;
  }

  private markFormCompleteAsFired(completionKey: string) {
    // Mark completion everywhere so future tabs/sessions skip it.
    this.formCompleteEventKeys.add(completionKey);

    const globalStoredKeys = this.getStoredGlobalFormCompleteKeys();
    if (!globalStoredKeys.includes(completionKey)) {
      globalStoredKeys.push(completionKey);
      this.saveStoredGlobalFormCompleteKeys(globalStoredKeys);
    }

    const storedKeys = this.getStoredFormCompleteKeys();
    if (!storedKeys.includes(completionKey)) {
      storedKeys.push(completionKey);
      this.saveStoredFormCompleteKeys(storedKeys);
    }
  }

  emitFormCompleteOnce(formType: string, pageName: string, routePath: string) {
    // Dedupe by request+formType so each form completion is tracked once.
    const requestKey =
      this.requestService.getRequestKey?.() ||
      this.requestService.requestKey ||
      '';
    const completionKey = `${requestKey}:${formType}:formComplete`;

    if (this.hasFormCompleteFired(completionKey)) {
      return false;
    }

    // Grab a lock so two tabs do not emit the same complete event together.
    const lockToken = this.tryAcquireFormCompleteLock(completionKey);
    if (!lockToken) {
      return false;
    }

    try {
      // Re-check once locked in case another tab finished moments earlier.
      if (this.hasFormCompleteFired(completionKey)) {
        return false;
      }

      const normalizedRoute = routePath.startsWith('/')
        ? routePath
        : `/${routePath}`;
      const url = `${globalThis.location.origin}${normalizedRoute}`;
      const previousUrl =
        this.adobeDataLayer?.page?.URL ||
        `${globalThis.location.origin}${globalThis.location.pathname}`;

      this.setAdobeDataLayer(
        {
          event: 'formComplete',
          page: {
            pageName,
            URL: url,
            fullURL: url,
            previousURL: previousUrl,
          },
        },
        RequestProgressStep.THANK_YOU,
      );

      // Persist marker only after successful analytics emission.
      this.markFormCompleteAsFired(completionKey);
      return true;
    } finally {
      this.releaseFormCompleteLock(completionKey, lockToken);
    }
  }

  overrideAndAddKeys(adobeDataLayer: any, data: any) {
    const newObj: any = {};
    for (const key in adobeDataLayer) {
      if (adobeDataLayer.hasOwnProperty(key)) {
        if (
          typeof adobeDataLayer[key] === 'object' &&
          typeof data[key] === 'object'
        ) {
          newObj[key] = this.overrideAndAddKeys(adobeDataLayer[key], data[key]);
        } else {
          newObj[key] = data.hasOwnProperty(key)
            ? data[key]
            : adobeDataLayer[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !adobeDataLayer.hasOwnProperty(key)) {
        newObj[key] = data[key];
      }
    }

    return newObj;
  }
  /**
  Call this function only after the user finishes signing. because the user can go back and forth between the options.
   */
  logNonDigitalElectionsEvent(formType: string, electionType: string) {
    Logger.log('logNonDigitalElectionsEvent', formType, electionType);
  }

  /*
    Call this function to track how many times the user clicks on download button.
  */
  logDownloadButtonClick(formType: string) {
    Logger.log('logDownloadButtonClick', formType);

    // Determine download details based on form type
    const downloadData =
      formType === FormType.PIP
        ? {
            fileName: 'PIP Documents.pdf',
            fileType: 'PIP Documents',
            fileFormat: 'pdf',
          }
        : formType === FormType.UM_UIM
          ? {
              fileName: 'UM/UIM Documents.pdf',
              fileType: 'UM/UIM Documents',
              fileFormat: 'pdf',
            }
          : {
              fileName: 'Documents.pdf',
              fileType: 'Documents',
              fileFormat: 'pdf',
            };

    // Send download event to Adobe Analytics
    this.setAdobeDataLayerDownloadEvent(downloadData);
  }

  /**
  Call this function only after the user finishes signing. because the user can go back and forth between the options.
   */
  logUIUMSelectionOfCoverages() {
    let bodilyInjury = this.uiumBaseRulesService.coverageTypeKey;
    let coverageType = this.uiumBaseRulesService.selectionTypeKey;
    let limitType = this.uiumBaseRulesService.limitTypeKey;
    let stackedOption = this.uiumBaseRulesService.stackTypeKey;

    Logger.log(
      'logUIUMSelectionOfCoverages',
      bodilyInjury,
      coverageType,
      limitType,
      stackedOption,
    );
  }

  /**
  Call this function only after the user finishes signing. because the user can go back and forth between the options.
   */
  logPIPSelectionOfCoverages() {
    let pipType = this.pipBaseRulesService.personalInjuryProtectionSelection;
    let pipAdditional = this.pipBaseRulesService.additionalOptions;
    let pipAcceptance = ' ';
    let pipCoverage = this.pipBaseRulesService.coverageLimit;

    Logger.log(
      'logPIPSelectionOfCoverages',
      pipType,
      pipAdditional,
      pipAcceptance,
      pipCoverage,
    );
  }
}
